// Phase 41 — Restored public landing hierarchy.
// Layers on top of the existing Explorer/whitelist rules — does NOT replace
// the current access model. Public visitors see the old showcase back in
// place. Two small controlled-access layers only: a "Prices coming soon"
// strip where pricing used to sit, and clear Join-the-Whitelist actions in
// the hero and after the samples. The bot Explorer gate + backend gate are
// unchanged.
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Check,
  ExternalLink,
  Layers,
  Mail,
  Minus,
  ShieldCheck,
  Sparkles,
  Trash2,
} from "lucide-react";
import UpstreamPositioningDiagram from "../components/UpstreamPositioningDiagram";
import LiveFusionExample from "../components/LiveFusionExample";
import LandingSampleWorkflows from "../components/LandingSampleWorkflows";
import NewInnovationTopicsSection from "../components/NewInnovationTopicsSection";
import RDBottleneckSection from "../components/RDBottleneckSection";
import IdeaFusionMicroPreview from "../components/IdeaFusionMicroPreview";
import QUICK_REPLY_SAMPLES from "../data/quickReplySamples";

const SUPPORT_EMAIL = "support@the-mindsteinlab.com";

// --- Grouped Quick Reply strip (top of page) ---
function QuickReplyStrip() {
  return (
    <section
      data-testid="landing-quick-reply-strip"
      className="max-w-6xl mx-auto px-4 sm:px-6 py-8"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
        QUICK REPLY · SAMPLE OUTPUTS
      </div>
      <h2 className="mt-2 text-xl sm:text-2xl font-mono tracking-tight text-white leading-tight">
        Two questions in. Two structured briefings out.
      </h2>
      <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed max-w-3xl">
        A snapshot of MindStein&apos;s fastest workflow. Both cards below are
        completed static examples — nothing here runs live.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {QUICK_REPLY_SAMPLES.map((s) => {
          const a = s.answer;
          return (
            <div
              key={s.id}
              data-testid={`landing-qr-strip-${s.id}`}
              className="border border-[#27272A] bg-[#0A0A0A] p-4"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-[0.2em] border border-amber-400/50 bg-amber-400/10 text-amber-200 px-1.5 py-0.5">
                  SAMPLE OUTPUT
                </span>
                <span className="text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
                  {s.kind.replace("_", " ")}
                </span>
              </div>
              <p className="mt-2 text-[12px] text-zinc-400 leading-snug">
                <span className="text-zinc-500 font-mono uppercase text-[9.5px] mr-1">
                  Q ·
                </span>
                {s.prompt}
              </p>
              <p className="mt-2 text-[13.5px] text-white leading-snug">
                {a.headline}
              </p>
              {(a.why_now || a.state_of_the_field) && (
                <p className="mt-2 text-[12.5px] text-zinc-300 leading-relaxed">
                  {a.why_now || a.state_of_the_field}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// --- "Prices coming soon" strip (replaces the old pricing block) ---
function PricesComingSoon() {
  return (
    <section
      data-testid="landing-prices-coming-soon"
      className="max-w-6xl mx-auto px-4 sm:px-6 py-6"
    >
      <div className="border border-[#27272A] bg-[#0A0A0A] p-4 flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300">
          PRICES · COMING_SOON
        </span>
        <span className="text-[12.5px] text-zinc-300 leading-relaxed">
          Access is free during controlled early access. Pricing tiers will
          be announced closer to public launch.
        </span>
        <Link
          to="/register"
          data-testid="landing-prices-cta"
          className="ml-auto inline-flex items-center gap-1 h-8 px-3 border border-emerald-400 bg-emerald-400 text-black text-[10.5px] font-mono uppercase tracking-wider hover:bg-emerald-300"
        >
          Join the whitelist <ArrowRight size={11} />
        </Link>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-mono">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#27272A] bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-[15px] font-extrabold tracking-tight text-white">
              THE_MINDSTEIN<span className="text-emerald-300">_LAB</span>
            </span>
            <span className="text-[9.5px] text-zinc-500 border border-[#27272A] px-1.5 py-0.5 tracking-wider">
              PRIVATE_PREVIEW
            </span>
          </Link>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider">
            <Link
              to="/login"
              data-testid="landing-cta-sign-in"
              className="h-8 px-3 flex items-center border border-[#27272A] text-zinc-200 hover:border-amber-400/60 hover:text-white"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="landing-cta-join-whitelist"
              className="h-8 px-3 flex items-center border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300"
            >
              Join the whitelist <ArrowRight size={12} className="ml-1" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid="landing-hero"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
      >
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-300">
          <Sparkles size={12} /> PRIVATE PREVIEW · CONTROLLED_EARLY_ACCESS
        </div>
        <h1 className="mt-3 text-3xl sm:text-5xl font-mono tracking-tight text-white leading-[1.1] max-w-4xl">
          A structured research layer for questions that don&apos;t fit a
          single database.
        </h1>
        <p className="mt-4 text-[15px] text-zinc-300 leading-relaxed max-w-3xl">
          MindStein Lab turns complex research questions into structured,
          source-grounded next steps. Verified Explorers can view the
          Intelligence Layer and completed workflow examples today. Live
          analysis opens through controlled early access.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider">
          <Link
            to="/register"
            data-testid="landing-cta-primary"
            className="h-10 px-4 inline-flex items-center gap-2 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300"
          >
            <Mail size={13} /> Join the whitelist <ArrowRight size={13} />
          </Link>
          <Link
            to="/login"
            className="h-10 px-4 inline-flex items-center gap-2 border border-[#27272A] text-zinc-200 hover:border-amber-400/60 hover:text-white"
          >
            Sign in <ArrowRight size={13} />
          </Link>
          <span className="text-zinc-500 normal-case tracking-normal text-[12px] font-sans">
            · No card. No credits. Email verification only.
          </span>
        </div>
      </section>

      {/* Problem / Solution — investor + user market fit at a glance */}
      <section
        data-testid="landing-problem-solution"
        className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 sm:pb-14"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div
            data-testid="landing-problem"
            className="border border-[#27272A] bg-[#0A0A0A] p-5 sm:p-6"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300">
              The Problem
            </div>
            <ul className="mt-4 space-y-3 text-[13px] text-zinc-300 leading-relaxed">
              {[
                "Research is scattered across tools and platforms.",
                "Synthesizing insights takes hours of manual work.",
                "Most AI tools summarize — they never connect ideas across sources.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Minus
                    size={14}
                    className="mt-[3px] text-amber-400/70 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="landing-solution"
            className="border border-emerald-400/30 bg-[#0A0F0C] p-5 sm:p-6"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
              The Solution
            </div>
            <ul className="mt-4 space-y-3 text-[13px] text-zinc-200 leading-relaxed">
              {[
                "MindStein cross-fuses research from multiple sources into a single synthesis.",
                "Surfaces patterns and connections other tools miss.",
                "Plugs into existing workflows as an upstream intelligence layer.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check
                    size={14}
                    className="mt-[3px] text-emerald-300 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p
          data-testid="landing-problem-solution-tagline"
          className="mt-5 sm:mt-6 text-[12px] sm:text-[13px] font-mono text-amber-200/80 tracking-tight leading-relaxed"
        >
          Built for researchers, founders, and teams who need synthesis — not
          just summaries.
        </p>
      </section>

      {/* Quick Reply samples grouped at the top */}
      <QuickReplyStrip />

      {/* Restored showcase — old sections back in their original positions */}
      <RDBottleneckSection />
      <UpstreamPositioningDiagram />
      <LiveFusionExample />
      <NewInnovationTopicsSection />

      {/* Original Sample Workflows detail (flagship Deep Synthesis + others) */}
      <LandingSampleWorkflows />

      {/* Prices coming-soon controlled-access layer */}
      <PricesComingSoon />

      {/* Clear Join-the-Whitelist section */}
      <section
        id="join-whitelist"
        data-testid="landing-join-whitelist-section"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-12"
      >
        <div className="border border-emerald-400/30 bg-emerald-500/[0.04] p-5 sm:p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
            JOIN THE WHITELIST
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white leading-tight">
            Verify your email. Explore MindStein today.
          </h2>
          <p className="mt-2 text-[13.5px] text-zinc-200 leading-relaxed max-w-3xl">
            Email verification unlocks the Intelligence Layer, the MindStein
            Workspace Preview, additional example material, and controlled
            early-access updates. When live Quick Reply opens, whitelist
            members are reviewed first.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wider">
            <Link
              to="/register"
              data-testid="landing-cta-whitelist"
              className="h-10 px-4 inline-flex items-center gap-2 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300"
            >
              <Mail size={13} /> Join the whitelist <ArrowRight size={13} />
            </Link>
            <Link
              to="/login"
              className="h-10 px-4 inline-flex items-center gap-2 border border-[#27272A] text-zinc-200 hover:border-amber-400/60 hover:text-white"
            >
              Sign in <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* What you get after verification (was Section 1 of Phase 39) */}
      <section
        data-testid="landing-section-available-now"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-8"
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
          AVAILABLE TO EXPLORE NOW
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white leading-tight">
          Intelligence Layer &amp; completed examples
        </h2>
        <p className="mt-3 text-[13.5px] text-zinc-300 leading-relaxed max-w-3xl">
          Verified Explorers browse the Intelligence Layer — ten topic
          libraries plus an innovation scout, each item carrying verified
          sources — and read completed Sample Outputs across Quick Reply,
          Cross-Fusion, and Deep Synthesis inside the Workspace Preview.
        </p>
        <ul className="mt-4 space-y-2 max-w-3xl">
          <li className="flex gap-3 text-[13px] leading-relaxed">
            <Layers size={14} className="shrink-0 mt-1 text-emerald-300" />
            <span>
              <span className="text-white">Intelligence Layer</span>{" "}
              <span className="text-zinc-400">— curated frontier research signals across ten topic libraries.</span>
            </span>
          </li>
          <li className="flex gap-3 text-[13px] leading-relaxed">
            <Brain size={14} className="shrink-0 mt-1 text-emerald-300" />
            <span>
              <span className="text-white">Workspace Preview</span>{" "}
              <span className="text-zinc-400">— completed Quick Reply, Cross-Fusion and Deep Synthesis examples, all labeled Sample Output.</span>
            </span>
          </li>
          <li className="flex gap-3 text-[13px] leading-relaxed">
            <ExternalLink size={14} className="shrink-0 mt-1 text-emerald-300" />
            <span>
              <span className="text-white">Roadmap updates</span>{" "}
              <span className="text-zinc-400">— controlled early-access announcements, no marketing spam.</span>
            </span>
          </li>
        </ul>
      </section>

      {/* Health/medical note */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        <p className="text-[11.5px] text-zinc-500 leading-snug">
          For health or medical topics: MindStein supports research workflow
          exploration only. It does not provide clinical guidance and does
          not replace expert review.
        </p>
      </div>

      {/* Mobile app strip — coming soon placeholders (to be hooked up later) */}
      <section
        data-testid="landing-mobile-apps"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-10"
      >
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5 flex flex-wrap items-center gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">
              MOBILE · COMING SOON
            </div>
            <p className="mt-1 text-[13px] text-zinc-300 leading-relaxed max-w-2xl">
              Native iOS and Android apps for MindStein Lab are on the way.
              Whitelist members will get the TestFlight and Play Console beta
              first.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a
              href="#"
              data-testid="landing-app-store"
              aria-disabled="true"
              onClick={(e) => e.preventDefault()}
              className="h-11 px-3 inline-flex items-center gap-2 border border-[#27272A] bg-[#0E0E0E] text-zinc-300 text-[11px] uppercase tracking-wider cursor-not-allowed"
              title="App Store · coming soon"
            >
              <span className="text-[9.5px] text-zinc-500">Download on the</span>
              <span className="font-mono">App&nbsp;Store</span>
            </a>
            <a
              href="#"
              data-testid="landing-google-play"
              aria-disabled="true"
              onClick={(e) => e.preventDefault()}
              className="h-11 px-3 inline-flex items-center gap-2 border border-[#27272A] bg-[#0E0E0E] text-zinc-300 text-[11px] uppercase tracking-wider cursor-not-allowed"
              title="Google Play · coming soon"
            >
              <span className="text-[9.5px] text-zinc-500">Get it on</span>
              <span className="font-mono">Google&nbsp;Play</span>
            </a>
          </div>
        </div>
      </section>

      {/* Powered by brainideasfused — Plugin Spotlight */}
      <section
        data-testid="landing-ideafusion-spotlight"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-10"
      >
        <div className="relative overflow-hidden border border-[#27272A] bg-gradient-to-br from-[#0B0B0B] via-[#0A0A0A] to-[#0E0A14] p-6 sm:p-8">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-fuchsia-500/10 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-amber-400/[0.06] blur-3xl"
          />

          <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-fuchsia-300">
                <Sparkles size={12} />
                Powered by brainideasfused
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-mono tracking-tight text-white leading-tight">
                Paste Reddit insights, get a fundable product brief in seconds.
              </h2>
              <p className="mt-3 text-[13px] sm:text-sm text-zinc-400 leading-relaxed">
                brainideasfused fuses scattered pain points into a sharp
                concept, target user, and value proposition — ready to pitch or
                build from.
              </p>
            </div>

            <div className="flex-shrink-0 md:pt-2">
              <a
                href="https://brainideasfused.online"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="landing-ideafusion-cta"
                className="group inline-flex items-center gap-2 h-11 px-5 bg-fuchsia-400 text-black font-mono text-[12px] uppercase tracking-[0.2em] hover:bg-fuchsia-300 transition-colors"
              >
                Try the plug &amp; go
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>
            </div>
          </div>

          <IdeaFusionMicroPreview />
        </div>
      </section>

      {/* Security Audit badge */}
      <section
        data-testid="landing-security-audit-badge"
        className="max-w-6xl mx-auto px-4 sm:px-6 pb-8"
      >
        <div className="border border-emerald-400/25 bg-emerald-400/[0.04] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start sm:items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-emerald-400/50 bg-emerald-400/10 shrink-0">
              <ShieldCheck size={16} className="text-emerald-300" />
            </span>
            <div>
              <div
                data-testid="landing-security-audit-title"
                className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300"
              >
                Security Audit Passed
              </div>
              <div className="mt-1 text-[13px] text-zinc-300 leading-snug">
                Full-stack security audit — auth, CORS, CSRF, rate limits,
                headers, secrets — all findings resolved.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
            <span
              data-testid="landing-security-audit-date"
              className="border border-[#27272A] bg-[#0A0A0A] px-2.5 py-1 text-emerald-200"
            >
              Passed · Sep 25, 2026
            </span>
            <span className="hidden sm:inline text-zinc-500">
              Independent read-only review
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#27272A] py-6 text-[10px] uppercase tracking-wider text-zinc-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <Trash2 size={10} /> RETAIN_30D
            </span>
            <span className="text-zinc-400">
              THE_MINDSTEIN_LAB · PRIVATE_PREVIEW
            </span>
            <Link
              to="/legal"
              data-testid="landing-link-legal"
              className="text-zinc-500 hover:text-amber-300"
            >
              Terms &amp; Conditions
            </Link>
            <Link
              to="/privacy"
              data-testid="landing-link-privacy"
              className="text-zinc-500 hover:text-emerald-300"
            >
              Privacy Policy
            </Link>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="landing-support-email"
              className="text-zinc-500 hover:text-amber-300"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
          <div
            data-testid="landing-copyright"
            className="pt-3 border-t border-[#161616] text-zinc-400"
          >
            © 2026 THE MINDSTEIN LAB · ALBERTA 2639389 LTD. · ALL RIGHTS RESERVED.
          </div>
        </div>
      </footer>
    </div>
  );
}
